import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Bell, CheckCheck, FileDown, Filter, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/lib/i18n";
import {
  listMyManualNotifications,
  markManualNotificationRead,
  markAllManualNotificationsRead,
  type ManualNotificationRow,
} from "@/lib/manual-notifications.functions";
import {
  getMyNotificationPrefs,
  updateMyNotificationPrefs,
  type NotificationPrefs,
} from "@/lib/notification-prefs.functions";

export const Route = createFileRoute("/notifikace")({
  ssr: false,
  component: NotificationsPage,
  head: () => ({
    meta: [
      { title: "Notifikace — Cerveza.International" },
      { name: "description", content: "Přehled notifikací o nových a aktualizovaných manuálech." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

function NotificationsPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { t, lang } = useT();
  const list = useServerFn(listMyManualNotifications);
  const markOne = useServerFn(markManualNotificationRead);
  const markAll = useServerFn(markAllManualNotificationsRead);
  const getPrefs = useServerFn(getMyNotificationPrefs);
  const savePrefs = useServerFn(updateMyNotificationPrefs);

  const [rows, setRows] = useState<ManualNotificationRow[]>([]);
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [prefs, setPrefs] = useState<NotificationPrefs>({ category: "manuals", in_app: true, email: false });
  const [savingPref, setSavingPref] = useState<"in_app" | "email" | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await list();
      setRows(r);
    } finally {
      setLoaded(true);
    }
  }, [list]);

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/auth", search: { redirect: "/notifikace" } });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    refresh();
    getPrefs().then(setPrefs).catch(() => { /* keep defaults */ });
  }, [user, refresh, getPrefs]);

  const changePref = async (key: "in_app" | "email", value: boolean) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    setSavingPref(key);
    try {
      const saved = await savePrefs({ data: { in_app: next.in_app, email: next.email } });
      setPrefs(saved);
      toast.success(t("notifPrefs.saved") || "Uloženo");
      if (key === "in_app") await refresh();
    } catch (e) {
      setPrefs(prefs);
      toast.error(e instanceof Error ? e.message : "Uložení selhalo");
    } finally {
      setSavingPref(null);
    }
  };

  const pick = (o: Record<string, string> | null | undefined) =>
    (o && (o[lang] || o.en || o.cs || Object.values(o)[0])) || "";

  const filtered = useMemo(
    () => (onlyUnread ? rows.filter((r) => !r.read_at) : rows),
    [rows, onlyUnread],
  );

  const unreadCount = rows.filter((r) => !r.read_at).length;

  const toggle = (id: string) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleAllVisible = () => {
    const visibleUnread = filtered.filter((r) => !r.read_at).map((r) => r.id);
    setSelected((s) => {
      const allSelected = visibleUnread.every((id) => s.has(id));
      const n = new Set(s);
      if (allSelected) visibleUnread.forEach((id) => n.delete(id));
      else visibleUnread.forEach((id) => n.add(id));
      return n;
    });
  };

  const markSelected = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    const ids = Array.from(selected);
    try {
      await Promise.all(ids.map((id) => markOne({ data: { id } }).catch(() => null)));
      const now = new Date().toISOString();
      setRows((rs) => rs.map((r) => (selected.has(r.id) && !r.read_at ? { ...r, read_at: now } : r)));
      setSelected(new Set());
    } finally {
      setBusy(false);
    }
  };

  const markAllUnread = async () => {
    setBusy(true);
    try {
      await markAll();
      const now = new Date().toISOString();
      setRows((rs) => rs.map((r) => ({ ...r, read_at: r.read_at ?? now })));
      setSelected(new Set());
    } finally {
      setBusy(false);
    }
  };

  if (loading || !user) {
    return <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">Načítám…</div>;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4 sm:p-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Settings className="h-4 w-4" />
            {t("notifPrefs.title") || "Nastavení notifikací"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            {t("notifPrefs.hint") || "Zvolte, kudy chcete dostávat oznámení o nových manuálech."}
          </p>
          <div className="flex items-center justify-between rounded-md border px-3 py-2">
            <div className="min-w-0">
              <Label htmlFor="pref-in-app" className="text-sm font-medium">
                {t("notifPrefs.inApp") || "V aplikaci"}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t("notifPrefs.inAppHint") || "Zobrazit notifikace ve zvonku a v tomto seznamu."}
              </p>
            </div>
            <Switch
              id="pref-in-app"
              checked={prefs.in_app}
              disabled={savingPref !== null}
              onCheckedChange={(v) => changePref("in_app", Boolean(v))}
            />
          </div>
          <div className="flex items-center justify-between rounded-md border px-3 py-2">
            <div className="min-w-0">
              <Label htmlFor="pref-email" className="text-sm font-medium">
                {t("notifPrefs.email") || "E-mail"}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t("notifPrefs.emailHint") || "Zasílat notifikace také na váš e-mail."}
              </p>
            </div>
            <Switch
              id="pref-email"
              checked={prefs.email}
              disabled={savingPref !== null}
              onCheckedChange={(v) => changePref("email", Boolean(v))}
            />
          </div>
          {!prefs.in_app && !prefs.email && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {t("notifPrefs.allOff") || "Notifikace jsou vypnuté. Nebudete dostávat žádná oznámení o nových manuálech."}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Bell className="h-5 w-5" />
            {t("notifPage.title") || "Notifikace"}
            {unreadCount > 0 && (
              <Badge variant="secondary" className="ml-1">
                {unreadCount} {t("notifPage.unread") || "nepřečtených"}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={onlyUnread ? "default" : "outline"}
              onClick={() => setOnlyUnread((v) => !v)}
              aria-pressed={onlyUnread}
            >
              <Filter className="mr-1 h-3.5 w-3.5" />
              {t("notifPage.onlyUnread") || "Jen nepřečtené"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-3 flex flex-wrap items-center gap-2 border-b pb-3">
            <Button
              size="sm"
              variant="outline"
              onClick={toggleAllVisible}
              disabled={filtered.every((r) => r.read_at)}
            >
              {t("notifPage.selectVisible") || "Vybrat viditelné nepřečtené"}
            </Button>
            <Button size="sm" onClick={markSelected} disabled={busy || selected.size === 0}>
              <CheckCheck className="mr-1 h-3.5 w-3.5" />
              {(t("notifPage.markSelected") || "Označit vybrané")} ({selected.size})
            </Button>
            <div className="flex-1" />
            <Button size="sm" variant="ghost" onClick={markAllUnread} disabled={busy || unreadCount === 0}>
              {t("notifPage.markAll") || "Označit vše jako přečtené"}
            </Button>
          </div>

          {!loaded ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Načítám…</div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              {onlyUnread
                ? t("notifPage.emptyUnread") || "Nemáte žádné nepřečtené notifikace."
                : t("notifPage.empty") || "Žádné notifikace."}
            </div>
          ) : (
            <ul className="divide-y">
              {filtered.map((n) => {
                const isUnread = !n.read_at;
                return (
                  <li key={n.id} className={`flex items-start gap-3 py-3 ${isUnread ? "" : "opacity-60"}`}>
                    <div className="pt-1">
                      <Checkbox
                        checked={selected.has(n.id)}
                        disabled={!isUnread}
                        onCheckedChange={() => toggle(n.id)}
                        aria-label="Vybrat notifikaci"
                      />
                    </div>
                    <FileDown className="mt-1 h-4 w-4 shrink-0 text-brand-terracotta" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Link
                          to="/ke-stazeni"
                          search={{ manual: n.slug || n.manual_id, lang, version: n.version }}
                          className="truncate text-sm font-medium hover:underline"
                          onClick={async () => {
                            if (isUnread) {
                              try { await markOne({ data: { id: n.id } }); } catch { /* ignore */ }
                              setRows((rs) => rs.map((r) => (r.id === n.id ? { ...r, read_at: new Date().toISOString() } : r)));
                            }
                          }}
                        >
                          {pick(n.title_i18n) || n.slug || n.manual_id}
                        </Link>
                        <Badge variant="outline" className="text-[10px]">v{n.version}</Badge>
                        {n.audience === "vakant_owners" && (
                          <Badge variant="secondary" className="text-[10px]">
                            {t("manualNotif.vakantOnly") || "Jen majitelé Vacantů"}
                          </Badge>
                        )}
                        {isUnread && <Badge className="text-[10px]">{t("notifPage.new") || "Nové"}</Badge>}
                      </div>
                      {pick(n.description_i18n) && (
                        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{pick(n.description_i18n)}</p>
                      )}
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        {new Date(n.created_at).toLocaleString()}
                      </div>
                    </div>
                    {isUnread && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          try { await markOne({ data: { id: n.id } }); } catch { /* ignore */ }
                          setRows((rs) => rs.map((r) => (r.id === n.id ? { ...r, read_at: new Date().toISOString() } : r)));
                        }}
                      >
                        {t("notifPage.markOne") || "Označit"}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
